import React, { useState } from 'react';
import { Box, Chip, Button, Drawer, Typography, useMediaQuery, useTheme } from '@mui/material';
import FilterListIcon from '@mui/icons-material/FilterList';
import useDesignTokens from './useDesignTokens';

// Pill chips with category dots, single or multi select. Collapses to one
// "Filters · N" button opening a bottom sheet below 900px. `options` items:
// { value, label, categoryDot?, dotColor? } — label already translated.
// `categoryDot` is a semantic key into categoryColors[mode]; `dotColor` is an
// escape hatch for admin-chosen category colours that don't map to one of
// the design's 7 keys (same reasoning as CategoryPill's `color` prop —
// CHANGES.md §2.8, "admin can still pick any colour").
const FilterChips = ({ options, value, onChange, multi = false, filtersLabel }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [sheetOpen, setSheetOpen] = useState(false);
  const { categoryColors } = useDesignTokens();

  const selectedValues = multi ? (value || []) : [value].filter((v) => v != null);
  const activeCount = selectedValues.filter((v) => v !== 'all').length;

  const isSelected = (optionValue) => (multi ? selectedValues.includes(optionValue) : value === optionValue);
  const hasAllOption = options.some((o) => o.value === 'all');

  const handleSelect = (optionValue) => {
    if (!multi) {
      onChange(optionValue);
      return;
    }
    const next = selectedValues.includes(optionValue)
      ? selectedValues.filter((v) => v !== optionValue)
      : [...selectedValues, optionValue];
    onChange(next);
  };

  // Only filter-style option lists (ones with an "all" to fall back to) get a
  // remove affordance on their active chip — a sort control's current choice
  // is always active and isn't something a user "clears".
  const handleRemove = (optionValue) => (multi ? handleSelect(optionValue) : onChange('all'));

  const renderChip = (option) => (
    <Chip
      key={option.value}
      label={option.label}
      onClick={() => handleSelect(option.value)}
      variant={isSelected(option.value) ? 'filled' : 'outlined'}
      color={isSelected(option.value) ? 'primary' : 'default'}
      icon={(option.categoryDot || option.dotColor) ? (
        <Box
          sx={{
            width: 7,
            height: 7,
            borderRadius: '50%',
            bgcolor: option.categoryDot ? (categoryColors[option.categoryDot] || categoryColors.none).dot : option.dotColor,
            ml: '10px',
          }}
        />
      ) : undefined}
    />
  );

  if (isMobile) {
    const activeOptions = options.filter((o) => isSelected(o.value) && o.value !== 'all');
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        <Button variant="outlined" startIcon={<FilterListIcon fontSize="small" />} onClick={() => setSheetOpen(true)} sx={{ alignSelf: 'flex-start' }}>
          {filtersLabel}{activeCount > 0 ? ` · ${activeCount}` : ''}
        </Button>
        {activeOptions.length > 0 && (
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {activeOptions.map((option) => (
              <Chip
                key={option.value}
                label={option.label}
                size="small"
                variant="filled"
                color="primary"
                onDelete={hasAllOption ? () => handleRemove(option.value) : undefined}
              />
            ))}
          </Box>
        )}
        <Drawer anchor="bottom" open={sheetOpen} onClose={() => setSheetOpen(false)}>
          <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Typography variant="subtitle1">{filtersLabel}</Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {options.map(renderChip)}
            </Box>
          </Box>
        </Drawer>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
      {options.map(renderChip)}
    </Box>
  );
};

export default FilterChips;
